import { computed, Injectable, signal } from '@angular/core';
import * as PrimeUIThemes from '@primeuix/themes';

import {
  DEFAULT_PRIMARY_THEME_ID,
  THEME_PRIMARY_THEME_FAMILY_OPTIONS,
  THEME_PRIMARY_THEME_OPTIONS,
  THEME_PRIMARY_THEME_OPTIONS_BY_FAMILY,
  ThemeFamily,
  ThemeFamilyOption,
  ThemeOption,
} from './theme-primary-themes';

const PRIMARY_THEME_STORAGE_KEY = 'app.theme.primaryTheme';

@Injectable({
  providedIn: 'root',
})
export class ThemeSelectionService {
  readonly themeFamilies: readonly ThemeFamilyOption[] = THEME_PRIMARY_THEME_FAMILY_OPTIONS;
  readonly themeOptions = THEME_PRIMARY_THEME_OPTIONS;
  readonly themeOptionsByFamily = THEME_PRIMARY_THEME_OPTIONS_BY_FAMILY;
  readonly defaultThemeId = DEFAULT_PRIMARY_THEME_ID;

  private readonly selectedThemeIdState = signal<string>(this.resolveInitialThemeId());

  readonly selectedThemeId = this.selectedThemeIdState.asReadonly();
  readonly selectedTheme = computed<ThemeOption>(() => this.requireTheme(this.selectedThemeIdState()));

  constructor() {
    const initialThemeId = this.selectedThemeIdState();
    const shouldPersistInitialTheme = this.findTheme(this.loadSavedThemeId())?.id === initialThemeId;
    this.applyTheme(initialThemeId, shouldPersistInitialTheme);
  }

  getThemesByFamily(family: ThemeFamily): ThemeOption[] {
    return [...this.themeOptionsByFamily[family]];
  }

  applyTheme(themeId: string, persist = true): void {
    const theme = this.findTheme(themeId);

    if (!theme) {
      return;
    }

    PrimeUIThemes.updatePreset({
      semantic: {
        primary: theme.themes.light.primary,
        colorScheme: {
          light: {
            surface: theme.themes.light.surface,
          },
          dark: {
            surface: theme.themes.dark.surface,
          },
        },
      },
    });

    this.selectedThemeIdState.set(theme.id);

    if (persist) {
      this.saveThemeId(theme.id);
    }
  }

  private resolveInitialThemeId(): string {
    const savedThemeId = this.loadSavedThemeId();
    return this.findTheme(savedThemeId)?.id ?? this.defaultThemeId;
  }

  private findTheme(themeId: string | null): ThemeOption | undefined {
    if (!themeId) {
      return undefined;
    }

    return this.themeOptions.find((theme) => theme.id === themeId);
  }

  private requireTheme(themeId: string): ThemeOption {
    return (
      this.findTheme(themeId) ??
      this.findTheme(this.defaultThemeId) ??
      this.themeOptions[0]
    );
  }

  private loadSavedThemeId(): string | null {
    if (typeof localStorage === 'undefined') {
      return null;
    }

    return localStorage.getItem(PRIMARY_THEME_STORAGE_KEY);
  }

  private saveThemeId(themeId: string): void {
    if (typeof localStorage === 'undefined') {
      return;
    }

    localStorage.setItem(PRIMARY_THEME_STORAGE_KEY, themeId);
  }
}
